import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, QuantityInput } from 'audamatic-ui';
import EbookModalNotification from '../sec_ebook-modal-notification/component';
import GiftcardModalNotification from '../sec_giftcard-modal-notification/component';
import ErrorMessage from '../ui_error-message/component.jsx';
import ExpressDelivery from '@components/ui_express-delivery/component';
import Restapi from '@utils/api';
import { addToCartGoogleTag } from '@utils/analytics.js';
import { getLineItems } from '@utils/basket';
import { getDevice } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';
import { routerHOC } from '@utils/hoc';
import OrderlineReference from '@components/ui_orderline-reference/component';
import Icon from '@components/base_svg-icon/component';
import { IS_B2B } from '@config';

import './styles/styles.scss';

/**
 * Add to Cart component
 *
 */
class AddToCartButton extends Component {
  constructor(props) {
    super(props);

    this.state = {
      buttonDisabled: false,
      status: '',
      loading: false,
      autoWidth: false,
      quantity: props.quantity || 1,
      amount: props.amount,
      hasError: false,
      expressOrder: false,
      reference: '',
      errorMessage: '',
      orderButtonType: this.props.product.offered === 1 ? 'primary' : 'tertiary',
    };

    this.restapi = new Restapi();
    this.restapi.setErrorFunction(this.handleError);
  }

  componentDidMount() {
    const { store } = this.props;
    store.events.subscribe('addToBasket', this.alreadyInBasket);
    this.setButtonType();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.loading !== this.state.loading) {
      return this.setState({
        autoWidth: false,
      });
    }
    if (prevProps.shouldFloat !== this.props.shouldFloat) {
      return this.setState({
        autoWidth: true,
      });
    }
    if (prevProps.quantity !== this.props.quantity) {
      return this.setState({ quantity: this.props.quantity });
    }
    if (prevProps.amount !== this.props.amount) {
      return this.setState({ amount: this.props.amount });
    }
    this.setButtonType();
  }

  setButtonType = () => {
    const { product } = this.props;
    /*
     * On a product change make sure to set the right state of the button.
     * When product is in cart but state not yet set to success, do so
     * When product is not in cart and state is set to succes, change to primary or tertiary
     * When product is not in cart and state is primary but should be tertiary, reset
     * When product is not in cart and state is tertiary but should be primary, reset
     */
    if (product.isProductInBasket && this.state.orderButtonType !== 'success') {
      this.setState({ orderButtonType: 'success' });
    } else if (!product.isProductInBasket && this.state.orderButtonType === 'success') {
      this.setState({ orderButtonType: product.offered === 1 ? 'primary' : 'tertiary' });
    } else if (!product.isProductInBasket && this.state.orderButtonType === 'primary' && product.offered !== 1) {
      this.setState({ orderButtonType: 'tertiary' });
    } else if (!product.isProductInBasket && this.state.orderButtonType === 'tertiary' && product.offered === 1) {
      this.setState({ orderButtonType: 'primary' });
    }
  };

  /**
   * The flag returned by product API
   * with the availability of a given product.
   */

  get productAdded() {
    const { product, store } = this.props;
    const { sku } = product;
    return store.getBasketProduct(sku);
  }

  get icon() {
    const { hideIcon, quickOrder, product } = this.props;
    if (hideIcon || quickOrder) {
      return '';
    } else if (product.isProductInBasket || this.state.orderButtonType === 'success') {
      return 'ico-check-verified';
    }
    return product.ctaIcon;
  }

  get disabled() {
    return this.props.disabled;
  }

  /*
   * Check if a product is already in the basket
   * If so, set the state of add to cart button to show success version
   */
  alreadyInBasket = () => {
    const inBasket = Object.keys(this.productAdded)?.length > 0;
    if (inBasket) {
      this.setState({
        orderButtonType: 'success',
      });
    }
  };

  /**
   * The button item is clicked
   * Now add an item to the basket
   */
  handleClick = (e) => {
    const { product } = this.props;
    const { offered, tbaproducttype, sku } = product;
    const { store, inModal } = this.props;

    e.preventDefault();
    if (tbaproducttype === 'eBook' && !inModal) {
      // The notification popup shown when the product is a eBook
      store.modal.openModal(
        'popup',
        'center',
        {
          title: t('ebook.modal.purchase-ebook'),
          component: <EbookModalNotification store={store} sku={sku} offered={offered} productType={tbaproducttype} product={product} />,
          classNames: 'modal-purchase-ebook',
        }
      );
    } else {
      // first get the active basket
      this.restapi.getActiveBasket({ origin: 'AddToCartButton' })
        .then(this.AddItemToCart);// then add an item to this basket
      this.setState({
        buttonDisabled: true,
        loading: true,
      });
      if (!!inModal) {
        store.modal.closeModal();
      }
    }
  };

  /**
   * Receives the flag offered and returns the proper text
   * to be displayed on order button
   *
   * @param {Number} offered
   */
  getBtnText(offered) {
    const { quickOrder, product } = this.props;

    const btnText = product.isEbook ? {
      1: <Text>ebook.cartButton.order</Text>,
      2: <Text>ebook.cartButton.reservation</Text>,
    } : {
      1: <Text>basket.addtocart</Text>,
      2: <Text>pdp.orderbutton.text.book</Text>,
      3: <Text>pdp.orderbutton.text.checkproviders</Text>,
    };

    const quickOrderText = t('comp.bookdetails.quickOrder.buttonText');
    return quickOrder ? quickOrderText : btnText[offered];
  }

  /**
   * Add the item with sku to the basket with id
   *
   * If it is not in the basket yet!
   *
   * @constructor
   * @param basket
   */
  AddItemToCart = (basket) => {
    const { reference, quantity, amount, expressOrder } = this.state;
    const { sku } = this.props.product;
    const { id = 0 } = basket;
    const lineItems = getLineItems(basket);
    let status = '';

    lineItems.forEach((attributes) => {
      if (attributes.sku === sku) {
        status = 'alreadyInBasket';
      }
    });

    if (status === 'alreadyInBasket') {
      this.props.store.events.publish('alreadyInBasket', { status });
      this.setState({
        status,
        buttonDisabled: false,
        loading: false,
      }, this.redirectToBasket);
    } else {
      status = 'addedToBasket';

      this.restapi.addItemToBasket({ basketId: id, sku, quantity, reference, amount, expressOrder })
        .then(() => this.setState({
          status,
          buttonDisabled: false,
          loading: false,
        }))
        .then(this.props.store.loadBasket)
        .then(() => this.props.store.events.publish('addToBasket', { status, sku }))
        .then(() => this.props.store.events.publish('refreshBasket', { origin: 'AddToCartButton' }))
        .then(() => this.props.store.events.publish('updateBasket', {}))
        .then(() => this.props.store.pushToDataLayer(addToCartGoogleTag(this.productAdded)))
        .then(() => {
          this.onAddedToBasket();
        })
        .catch(({ msg, type, message }) => {
          const error = new Error(msg);
          error.type = type || '';
          error.message = message || '';
          this.handleError(error);
          this.setState({ status, loading: false });
        });
    }
  };

  onAddedToBasket() {
    const { sku } = this.props.product;
    if (this.props.onAddedToBasket) {
      this.props.onAddedToBasket(sku);
    }
    this.redirectToBasket();
  }

  /**
   * Redirect user to the checkout if the
   * quickOrder prop has been provided.
   */
  redirectToBasket = () => {
    const { store, quickOrder } = this.props;
    if (!!quickOrder) {
      window.location.href = store.urls.cart;
    }
  };

  /**
   * @todo: refactor this erreor messages
   *
   * @param error
   */
  handleError = (error) => {
    const { store } = this.props;
    let text = <Text html>checkout.error.default</Text>;
    let title = t('checkout.title.error');
    let component;
    let classNames;
    let popupPosition = 'left';

    switch (error.type) {
      case 'product.ebook.not_orderable.error':
        text = t('product.ebook.not_orderable.error');
        break;
      case 'basketvalidation.error.MaxItemSizeExceeded':
        // Notification shown when customer wants to add more lineitems to basket than allowed
        text = t('basketvalidation.error.MaxItemSizeExceeded');
        break;
      case 'basket.product.not_allowed_with_giftcard.error':
        // The notification popup shown when a giftcard is purchased while the basket already contains another giftcard.
        title = t('giftcard.modal.notification-title');
        component = <GiftcardModalNotification store={store} />;
        classNames = 'modal-giftcard-notification';
        popupPosition = 'center';
        break;
      default:
        if (error.type && error.message) {
          text = error.message;
        }
    }

    if (error.refresh === 'true') {
      component = (
        <span>{text}
          <p>
            <Button
              variant="primary"
              onClick={() => window.location.href = window.location.href}
            >
              <Text>checkout.button.refresh</Text>
            </Button>
          </p>
        </span>
      );
    }

    this.props.store.modal.openModal(
      'popup',
      popupPosition,
      {
        title,
        text,
        component,
        classNames,
      }
    );

    this.setState({
      buttonDisabled: false,
      loading: false,
    });
  };

  get name() {
    const { product } = this.props;
    const { name } = product;
    let displayName = '';
    switch (this.state.status) {
      case '':
        displayName = name || '';
        break;
      case 'alreadyInBasket':
        displayName = t('checkout.button.alreadyInBasket');
        break;
      case 'addedToBasket':
        displayName = t('checkout.button.addedToBasket');
        break;
    }
    return displayName;
  }

  get buttonText() {
    const { offered } = this.props.product;
    const label = (this.props.children && this.props.children.trim()) ? this.props.children : this.getBtnText(offered);
    return <span style={{ display: 'content' }}>{label}</span>;
  }


  /**
   * This is returning the content of the buttons, depending on the screen size.
   * Currently on small screens we show the price (in a floating container)
   * @return {{children}}
   *
   */

  getChildren = () => {
    const { product, hideText } = this.props;
    if (hideText) {
      return;
    }

    let { price1, price2 } = product.getProductPrice();

    //If a price ends with .00 change it to .-
    if (typeof price1 === 'string' && price1.endsWith('.00')) {
      price1 = price1.replace('.00', '.-');
    } else if (typeof price2 === 'string' && price2.endsWith('.00')) {
      price2 = price2.replace('.00', '.-')
    }

    const floatOnMobile = (getDevice().isMobile && this.props.shouldFloat === true);
    return {
      children: floatOnMobile ? (price2 || price1) : this.buttonText,
    };
  };

  widthRefreshClass = () => {
    return this.state.autoWidth ? 'autoWidth' : '';
  };

  getQuantity = (quantity) => {
    this.setState({ quantity });
  };

  updateExpressValue = (value) => {
    this.setState({ expressOrder: value });
    this.props.toggleExpressOrder(value);
  };

  updateReference = (value) => {
    this.setState({ reference: value });
  };

  get errorMessageMaxQuantity() {
    return (<Text replacements={{ quantity: this.props.product.maxOrder }}>quantity.error.message</Text>);
  }

  showErrorMessageOrderline = (hasError) => {
    if (hasError) {
      this.setState({ errorMessage: <Text>orderline-reference.errormessage</Text> });
    } else {
      this.setState({ errorMessage: '' });
    }
  };

  render() {
    const { size = 'large', product, showExpressOrder = false } = this.props;
    if (!product.showAddToCartButton) {
      return null;
    }
    if (IS_B2B) {
      if (product.isCBProduct && !product.isOrderableCBProduct) {
        return (
          <Text>usergroups.CB</Text>
        );
      } else if (!product.isOrderable) {
        return (
          <div className="not-orderable-text">
            <Text html>usergroups.notOrderable</Text>
          </div>
        );
      }
    }

    return (
      <div className="add-to-cart-button">
        {!product.isEbook && (
          <>
            {this.props.showOrderlineReference && (
              <OrderlineReference
                stacked={true}
                wide={true}
                value={this.state.reference}
                changeReference={this.updateReference}
                showErrorMessage={this.showErrorMessageOrderline}
              />
            )}
            <ExpressDelivery updateValue={this.updateExpressValue} showExpressOrder={showExpressOrder && product.expressOrder} />
          </>
        )}
        <div className="button-flex">
          {this.props.showQuantity && !product.isEbook && (
            <QuantityInput
              changeQuantity={this.getQuantity}
              errorMessage={this.errorMessageMaxQuantity}
              maxQuantity={product.maxOrder}
              value={this.state.quantity}
            />
          )}
          <div className={`btn-wrapper ${this.widthRefreshClass()}`}>
            <Button
              variant={this.state.orderButtonType}
              size={size}
              iconLeft={<Icon name={this.icon} size={this.props.smallIcon ? 100 : 150} className="no-margin" />}
              loading={this.state.loading}
              disabled={this.state.buttonDisabled || this.disabled}
              fullWidth={true}
              onClick={this.handleClick}
              name={this.name}
              data-test-id={this.props.dataTestId || 'button-addToCart'}
              {...this.getChildren()}
            />
          </div>
        </div>
        {this.state.errorMessage !== '' && (
          <ErrorMessage>
            {this.state.errorMessage}
          </ErrorMessage>
        )}
      </div>
    );
  }
}

AddToCartButton.defaultProps = {
  disabled: false,
  product: {},
};

AddToCartButton.propTypes = {
  /**
   * The title of the button. You see this when you hover over the button
   */
  name: PropTypes.string,
  /**
   * The before and after price
   */
  prices: PropTypes.object,
  /**
   * The data-test-id that will be added to the button
   */
  dataTestId: PropTypes.string,
  /**
   * The SKU of the element
   */
  sku: PropTypes.string,
  /**
   * The flag returned by product API
   * with the availability of a given product.
   */
  offered: PropTypes.number,
  /**
   * callback when product is added to the basket
   */
  onAddedToBasket: PropTypes.func,
  /**
   * The product type of the product
   */
  productType: PropTypes.string,
  /**
   * Show icon
   */
  icon: PropTypes.string,
  /**
   * Show small icon
   */
  smallIcon: PropTypes.bool,
  /**
   * the store
   */
  store: PropTypes.object.isRequired,
  /**
   * force disabled state of the button
   */
  disabled: PropTypes.bool,
  /**
   * Check if ebook notification modal is opened
   */
  inModal: PropTypes.bool,
  /**
   *The product
   */
  product: PropTypes.object,
  children: PropTypes.node,
  /**
   * If quickOrder is set to true, the user is immediately
   * redirected to the checkout on clicking the button.
   */
  quickOrder: PropTypes.bool,
  /*
   * Show different button on PDP
   */
  shouldFloat: PropTypes.bool,
  size: PropTypes.string,
  history: PropTypes.object,
  pdp: PropTypes.bool,
  hideIcon: PropTypes.bool,
  hideText: PropTypes.bool,
  quantity: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  amount: PropTypes.string,
  // Show or hide the quantity input
  showQuantity: PropTypes.bool,
  // Show or hide the express-ordr toggle (only b2b)
  showExpressOrder: PropTypes.bool,
  showOrderlineReference: PropTypes.bool,
  updateValue: PropTypes.bool,
  toggleExpressOrder: PropTypes.func,
};

export const ExtendableAddToCartButton = AddToCartButton;

export default routerHOC(AddToCartButton);
